import {useEffect, useRef} from 'react';
import {useSearchParams} from 'react-router-dom';
import {apiClient} from '../../http/query-client';
import {getCookie, setCookie} from './use-cookie';

export const TRACKING_PARAMETERS_COOKIE = 'TRACKING_PARAMETERS';

// ad click ids worth keeping: Google Ads (gclid) and the pop networks'
// click macros (PropellerAds sends clickid, Adsterra click_id). A query
// string without one of them is left alone, so an ordinary link never
// replaces a stored click.
export const CLICK_ID_PARAMS = ['gclid', 'clickid', 'click_id'];

export type TrackingParameters = Record<string, string>;

export function getTrackingParametersFromCookie():
  | TrackingParameters
  | undefined {
  const raw = getCookie(TRACKING_PARAMETERS_COOKIE);
  if (!raw) return undefined;

  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object') {
      return parsed as TrackingParameters;
    }
  } catch {
    // ignore invalid cookie value
  }

  return undefined;
}

export function setTrackingParametersCookie(params: TrackingParameters): void {
  if (!params || Object.keys(params).length === 0) return;
  setCookie(TRACKING_PARAMETERS_COOKIE, JSON.stringify(params));
}

export function persistTrackingParametersFromCookie(): void {
  const params = getTrackingParametersFromCookie();
  if (!params || Object.keys(params).length === 0) return;
  apiClient.post('users/me/tracking-parameters', {
    tracking_parameters: params,
  });
}

export function captureTrackingParametersFromSearchParams(
  searchParams: URLSearchParams,
  options: {requireClickId?: boolean} = {},
): TrackingParameters | undefined {
  const params = Object.fromEntries(searchParams.entries());
  if (Object.keys(params).length === 0) return undefined;
  if (
    options.requireClickId &&
    !CLICK_ID_PARAMS.some(key => searchParams.get(key))
  ) {
    return undefined;
  }
  setTrackingParametersCookie(params);
  return params;
}

export function useTrackingParameters(options: {
  requireClickId?: boolean;
  onCapture?: (params: TrackingParameters) => void;
} = {requireClickId: true}) {
  const [searchParams] = useSearchParams();
  const requireClickId = options.requireClickId ?? true;
  const onCapture = options.onCapture;
  const lastCaptureRef = useRef<string | null>(null);

  useEffect(() => {
    const captured = captureTrackingParametersFromSearchParams(searchParams, {
      requireClickId,
    });
    if (!captured || !onCapture) return;
    const captureKey = JSON.stringify(captured);
    if (lastCaptureRef.current === captureKey) return;
    lastCaptureRef.current = captureKey;
    onCapture(captured);
  }, [searchParams, requireClickId, onCapture]);
}
